import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiAlertCircle,
  FiCreditCard,
  FiRefreshCw,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';
import { getLocale, t, useI18n } from '../../i18n';
import {
  billingService,
  type BillingSummary,
  type BillingTrueUp,
  type BillingUsagePeriod,
  type TaxTreatment,
} from '../../services/billingService';
import './BillingPage.css';

/**
 * The live counter. The point of this page is that the invoice never
 * introduces a number the customer has not already seen, so it shows the
 * current count, what it will cost, and what was measured in every closed
 * period behind it.
 */

function money(cents: number | null | undefined, currency: string): string {
  if (cents === null || cents === undefined) return '—';
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function monthLabel(value: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(getLocale(), { month: 'long', year: 'numeric' });
}

function dayLabel(value: string | null): string {
  if (!value) return t('Not yet');
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return t('Not yet');
  return parsed.toLocaleDateString(getLocale(), { day: '2-digit', month: 'short', year: 'numeric' });
}

function taxLabel(treatment: TaxTreatment): string {
  switch (treatment) {
    case 'uk_reverse_charge':
      return t('Invoiced under the UK reverse charge. VAT is accounted for by you.');
    case 'eu_reverse_charge':
      return t('Invoiced under the intra-community reverse charge against your VAT number.');
    default:
      return t('Irish VAT is added to the amounts shown.');
  }
}

function statusLabel(status: BillingUsagePeriod['status']): string {
  switch (status) {
    case 'reported': return t('Sent to billing');
    case 'invoiced': return t('Invoiced');
    case 'void': return t('Void');
    default: return t('Closed');
  }
}

function trueUpStatusLabel(status: BillingTrueUp['status']): string {
  switch (status) {
    case 'invoiced': return t('Invoiced');
    case 'waived': return t('Waived');
    case 'void': return t('Void');
    default: return t('Pending');
  }
}

export default function BillingPage() {
  const { t: translate } = useI18n();
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [periods, setPeriods] = useState<BillingUsagePeriod[]>([]);
  const [trueUps, setTrueUps] = useState<BillingTrueUp[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setIsBusy(true); else setIsLoading(true);
    try {
      const [nextSummary, nextPeriods, nextTrueUps] = await Promise.all([
        billingService.fetchSummary(),
        billingService.fetchUsageHistory(),
        billingService.fetchTrueUps(),
      ]);
      setSummary(nextSummary);
      setPeriods(nextPeriods);
      setTrueUps(nextTrueUps);
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : translate('Unable to load billing details'));
    } finally {
      setIsLoading(false);
      setIsBusy(false);
    }
  }, [translate]);

  useEffect(() => { void load(); }, [load]);

  const currency = summary?.currency ?? 'eur';
  const isAnnual = summary?.billingInterval === 'year';

  // Above the floor the count is real usage; at or below it the floor is what
  // is charged, and saying so plainly is the whole point of the counter.
  const flooredByMinimum = useMemo(
    () => Boolean(summary && summary.activeUsers < summary.floorUsers),
    [summary],
  );

  if (isLoading) {
    return (
      <div className="billing-page billing-page--centred">
        <div className="spinner" aria-label={translate('Loading')} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="billing-page">
        <div className="billing-page__error" role="alert">
          <FiAlertCircle aria-hidden="true" />
          <span>{error}</span>
          <button type="button" className="btn btn--secondary" onClick={() => void load(true)}>
            {translate('Try again')}
          </button>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="billing-page">
        <div className="billing-page__error" role="status">
          <FiCreditCard aria-hidden="true" />
          <span>{translate('Billing details are not available for this account.')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="billing-page">
      <header className="billing-page__header">
        <div>
          <span className="billing-page__eyebrow">{translate('Billing')}</span>
          <h1 className="billing-page__title">
            {translate('{plan} plan', { plan: summary.planName })}
          </h1>
          <p className="billing-page__subtitle">
            {isAnnual
              ? translate('Billed annually, measured monthly, settled each quarter.')
              : translate('Billed each month for the users who were active in it.')}
          </p>
        </div>
        <button
          type="button"
          className="btn btn--secondary"
          onClick={() => void load(true)}
          disabled={isBusy}
        >
          <FiRefreshCw aria-hidden="true" />
          {isBusy ? translate('Refreshing…') : translate('Refresh')}
        </button>
      </header>

      <section className="billing-counter" aria-label={translate('Current period')}>
        <div className="billing-counter__main">
          <span className="billing-counter__label">
            <FiUsers aria-hidden="true" />
            {translate('Billable users this period')}
          </span>
          <span className="billing-counter__value">{summary.billableUsers}</span>
          <span className="billing-counter__meta">
            {monthLabel(summary.periodStart)}
            {' · '}
            {translate('{count} days left', { count: summary.daysRemaining })}
          </span>
        </div>

        <dl className="billing-counter__breakdown">
          <div>
            <dt>{translate('Guards who clocked in')}</dt>
            <dd>{summary.activeGuards}</dd>
          </div>
          <div>
            <dt>{translate('Admins and managers who signed in')}</dt>
            <dd>{summary.activeStaff}</dd>
          </div>
          <div>
            <dt>{translate('Distinct active users')}</dt>
            <dd>{summary.activeUsers}</dd>
          </div>
          <div>
            <dt>{translate('Plan minimum')}</dt>
            <dd>{summary.floorUsers}</dd>
          </div>
        </dl>

        <div className="billing-counter__projection">
          <span className="billing-counter__projection-label">
            {isAnnual ? translate('Projected at this rate, per year') : translate('Projected this month')}
          </span>
          <strong className="billing-counter__projection-value">
            {money(summary.projectedAmountCents, currency)}
          </strong>
          <span className="billing-counter__projection-unit">
            {summary.unitAmountCents === null
              ? translate('No active price for this plan')
              : isAnnual
                ? translate('{price} per user per year, excluding VAT', {
                  price: money(summary.unitAmountCents, currency),
                })
                : translate('{price} per user per month, excluding VAT', {
                  price: money(summary.unitAmountCents, currency),
                })}
          </span>
        </div>
      </section>

      <p className="billing-page__note">
        {flooredByMinimum
          ? translate('Fewer than {count} users have been active, so this period bills at the {count}-user minimum.', { count: summary.floorUsers })
          : translate('Above the {count}-user minimum, so you are billed for the {billable} distinct people who used Odinseye this period.', { count: summary.floorUsers, billable: summary.billableUsers })}
        {' '}
        {translate('A guard is counted once they clock into a shift, never for being on the roster. Deactivate a leaver from their Staff profile and they stop counting from the next period.')}
      </p>

      <p className="billing-page__note billing-page__note--tax">
        {taxLabel(summary.taxTreatment)}
        {summary.vatNumber ? ` ${translate('VAT number on file: {number}', { number: summary.vatNumber })}` : ''}
      </p>

      {isAnnual && (
        <section className="billing-panel" aria-label={translate('Annual commitment')}>
          <h2 className="billing-panel__title">
            <FiTrendingUp aria-hidden="true" />
            {translate('Annual commitment and quarterly true-up')}
          </h2>
          <p className="billing-panel__lede">
            {summary.committedUserCount === null
              ? translate('No committed user count is recorded, so the plan minimum is used as the baseline.')
              : translate('You committed to {count} users. Each quarter we compare the average of the three measured months against that baseline and invoice only the excess, so one busy month does not land whole.', { count: summary.committedUserCount })}
          </p>

          {trueUps.length === 0 ? (
            <p className="billing-panel__empty">{translate('No quarter has been settled yet.')}</p>
          ) : (
            <div className="billing-table-wrap">
              <table className="billing-table">
                <thead>
                  <tr>
                    <th>{translate('Quarter')}</th>
                    <th>{translate('Committed')}</th>
                    <th>{translate('Average')}</th>
                    <th>{translate('Peak month')}</th>
                    <th>{translate('Charged for')}</th>
                    <th>{translate('Amount')}</th>
                    <th>{translate('Status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {trueUps.map((trueUp) => (
                    <tr key={trueUp.id}>
                      <td>{monthLabel(trueUp.quarterStart)}</td>
                      <td>{trueUp.committedUsers}</td>
                      <td>{trueUp.averageUsers.toFixed(1)}</td>
                      <td className="billing-table__muted">{trueUp.peakUsers}</td>
                      <td>{trueUp.chargeableUsers}</td>
                      <td>{money(trueUp.amountCents, trueUp.currency)}</td>
                      <td>{trueUpStatusLabel(trueUp.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="billing-panel" aria-label={translate('Usage history')}>
        <h2 className="billing-panel__title">{translate('Closed periods')}</h2>
        <p className="billing-panel__lede">
          {translate('What was measured in each period that has already closed. These are the figures billing works from.')}
        </p>

        {periods.length === 0 ? (
          <p className="billing-panel__empty">
            {translate('Nothing has closed yet. The first period closes at the start of next month.')}
          </p>
        ) : (
          <div className="billing-table-wrap">
            <table className="billing-table">
              <thead>
                <tr>
                  <th>{translate('Period')}</th>
                  <th>{translate('Guards')}</th>
                  <th>{translate('Staff')}</th>
                  <th>{translate('Distinct users')}</th>
                  <th>{translate('Billed for')}</th>
                  <th>{translate('Amount')}</th>
                  <th>{translate('Status')}</th>
                  <th>{translate('Sent')}</th>
                </tr>
              </thead>
              <tbody>
                {periods.map((period) => (
                  <tr key={period.id}>
                    <td>{monthLabel(period.periodStart)}</td>
                    <td>{period.activeGuards}</td>
                    <td>{period.activeStaff}</td>
                    <td>{period.activeUsers}</td>
                    <td>
                      {period.billableUsers}
                      {period.activeUsers < period.floorUsers && (
                        <span className="billing-table__tag">{translate('minimum')}</span>
                      )}
                    </td>
                    <td>{money(period.amountCents, period.currency)}</td>
                    <td>{statusLabel(period.status)}</td>
                    <td className="billing-table__muted">{dayLabel(period.reportedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="billing-page__footnote">
        {translate('All amounts exclude VAT. To change plan, add a purchase order, or discuss volume pricing, email')}{' '}
        <a href="mailto:sales@odinseye.eu">sales@odinseye.eu</a>.
      </p>
    </div>
  );
}
